/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Box,
  H3,
  FormControl,
  TextInput,
  Button,
  Checkbox,
} from '@ouster/webviz-react-components';

import { FormEvent, useEffect, useState } from 'react';

import styles from './Profile.module.css';
import useChakraHook from '../../hooks/useChakraHook';
import { useStoreState, useStoreActions } from '../../reducers';

type ProfileProps = {
  handleClose: () => void;
};

type OrganizationListProps = {
  title: string;
  organizations: Array<{ id: number; name: string; role?: string }>;
  onOrgClick: (id: number) => void;
  currentOrgId?: number;
};

const OrganizationList = ({
  title,
  organizations,
  onOrgClick,
  currentOrgId,
}: OrganizationListProps) => (
  <Box>
    <H3 fontWeight={600} marginBottom="0.75em" fontSize={16}>
      {title}
    </H3>
    <ul className={styles['org-list']}>
      {organizations.map((org) => (
        <li key={org.id}>
          <Button
            variant="ghost"
            colorScheme={org.id === currentOrgId ? 'yellow' : 'gray'}
            onClick={() => onOrgClick(org.id)}
            paddingBlock={1}
            disabled={org.id === currentOrgId}
          >
            {org.name}
            {org.role && <span>({org.role.toLowerCase()})</span>}
            {org.id === currentOrgId && <span>- Current</span>}
          </Button>
        </li>
      ))}
    </ul>
  </Box>
);

export default function Profile({ handleClose }: ProfileProps) {
  const { user, appUser, userOrgs, userOwnOrgs, curUserOrg, isOusterUser } =
    useStoreState((storeState) => storeState.user);
  const {
    fetchUserOwnOrgs,
    fetchUserOrgs,
    setCurUserOrg,
    updateUserName,
    updateUserMarketingPreference,
  } = useStoreActions((actions) => actions.user);
  const { createOrg, ousterUserCreateOrg } = useStoreActions(
    (actions) => actions.organization
  );

  const [creatingOrg, setCreatingOrg] = useState(false);

  const { showToast } = useChakraHook();

  useEffect(() => {
    if (user?.identity_id) {
      fetchUserOwnOrgs(user.identity_id);
    }
  }, [fetchUserOwnOrgs, user?.identity_id]);

  const INITIAL = appUser?.name[0].toUpperCase() ?? 'U';

  const joinedOrgsExcludingOwn = userOrgs.filter(
    (org) => !userOwnOrgs.find((ownOrg) => ownOrg.id === org.id)
  );

  async function handleNameChange(e: FormEvent) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const displayName = formData.get('displayName') as string;

    try {
      if (!user) return;
      await updateUserName({
        name: displayName,
        identity_id: user?.identity_id,
      });

      showToast({
        title: 'Success',
        description: 'Your display name has been successfully updated.',
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to update display name',
        description: `${error}`,
        status: 'error',
      });
    }
  }

  async function handleOrgCreation(e: FormEvent) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const orgName = formData.get('orgName') as string;
    try {
      setCreatingOrg(true);
      if (isOusterUser) {
        await ousterUserCreateOrg({ orgName });
      } else {
        await createOrg(orgName);
      }
      fetchUserOwnOrgs(user!.identity_id);
      fetchUserOrgs(user!.identity_id);

      form.reset();

      showToast({
        title: 'Congratulation!',
        description: 'Your organization has been successfully created.',
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to create organization',
        description: `${error}`,
        status: 'error',
      });
    } finally {
      setCreatingOrg(false);
    }
  }

  const switchOrganization = (id: number) => {
    setCurUserOrg(id);
    handleClose();
  };

  const handleMarketingPreferenceChange = async (checked: boolean) => {
    try {
      await updateUserMarketingPreference({
        identity_id: user!.identity_id,
        opt_out: !checked,
      });
      showToast({
        title: 'Success',
        description: 'Your marketing preferences have been updated.',
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to update marketing preferences',
        description: `${error}`,
        status: 'error',
      });
    }
  };

  return (
    <div className={styles.container} data-testid="profile-root">
      <Box display="grid" gap={4} alignContent="start" justifyItems="center">
        <div className={styles.avatar} data-testid="profile-avatar">
          {INITIAL}
        </div>
        <Box
          fontWeight={500}
          color="gray.200"
          textAlign="center"
          lineHeight={1.4}
          data-testid="profile-current-org"
          wordBreak="break-all"
        >
          {curUserOrg?.name}
        </Box>
      </Box>

      <div className={styles['profile-info']}>
        <Box>
          <H3 fontWeight={600} marginBottom="0.75em" fontSize={16}>
            Display Name
          </H3>
          <form
            onSubmit={handleNameChange}
            data-testid="profile-displayname-form"
          >
            <FormControl paddingInline={0}>
              <div className={styles['inline-form']}>
                <TextInput
                  type="text"
                  defaultValue={appUser?.name}
                  borderRadius={4}
                  name="displayName"
                  isRequired
                />
                <Button minWidth="120px" variant="outline" type="submit">
                  Change
                </Button>
              </div>
            </FormControl>
          </form>
        </Box>

        <Box>
          <H3 fontWeight={600} marginBottom="0.75em" fontSize={16}>
            Email
          </H3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: '1rem',
            }}
          >
            <Box color="gray.200" data-testid="profile-email">
              {appUser?.email}
            </Box>
            {appUser && appUser.email_opt_out !== null && (
              <Checkbox
                label="Opt in to marketing communications"
                isChecked={!appUser?.email_opt_out}
                onChange={async (e) => {
                  await handleMarketingPreferenceChange(e.target.checked);
                }}
                data-testid="profile-marketing-checkbox"
              />
            )}
          </div>
        </Box>

        {/* user who hasn't created an organization or Ouster user has the right to create organization */}
        {(userOwnOrgs.length === 0 || isOusterUser) && (
          <Box>
            <H3 fontWeight={600} fontSize={16}>
              Create an Organization
            </H3>
            {!isOusterUser && (
              <Box>
                <small>Each user can only create one organization</small>
              </Box>
            )}
            <form
              onSubmit={handleOrgCreation}
              data-testid="profile-create-org-form"
            >
              <FormControl paddingInline={0} marginTop="0.75em">
                <div className={styles['inline-form']}>
                  <TextInput
                    type="text"
                    placeholder="Name your own organization"
                    borderRadius={4}
                    name="orgName"
                    isRequired
                  />
                  <Button
                    minWidth="120px"
                    variant="outline"
                    type="submit"
                    disabled={creatingOrg}
                  >
                    Create
                  </Button>
                </div>
              </FormControl>
            </form>
          </Box>
        )}
        {userOwnOrgs.length > 0 && (
          <OrganizationList
            title="Created Organizations"
            organizations={userOwnOrgs}
            onOrgClick={switchOrganization}
            currentOrgId={curUserOrg?.id}
          />
        )}
        <OrganizationList
          title="Joined Organizations"
          organizations={joinedOrgsExcludingOwn}
          onOrgClick={switchOrganization}
          currentOrgId={curUserOrg?.id}
        />
      </div>
    </div>
  );
}
